import React, { createContext, useContext } from 'react';

/**
 * Provider-less default: `undefined` means "no repeater scope active".
 * {@link useItemScope} falls back to this value so `source: 'item'`
 * bindings outside a repeater resolve to `undefined` without throwing.
 */
const ItemScopeContext = createContext<unknown>(undefined);

/**
 * Stable per-row identity path, surfaced separately from the item
 * VALUE so existing `useItemScope()` consumers (the binding resolver)
 * are untouched.
 *
 * Why this exists (the per-row fire-once fix): a
 * `platform.dataRepeater` renders the SAME `itemSlot` template — with
 * the SAME Puck node ids — once per row. `createBoundRender` derives
 * the `Trackable` fire-once `stableKey` from
 * `dslConfigId::stepIndex::puckNodeId`, which is therefore identical
 * for every repeated card. Without a per-row discriminator the
 * module-level `FIRED_STABLE_KEYS` dedup collapses an entire repeated
 * section (e.g. a "More matches" result list) onto a single
 * `ProductViewed` — every card after the first is silently dropped.
 *
 * The provider composes the path so nested repeaters stay unique
 * (`"0.2"` = item 2 of the repeater inside row 0 of an outer
 * repeater). `undefined` means "no repeater scope" — `stableKey`
 * falls back to its non-repeater shape and behaviour is unchanged
 * outside a repeater.
 */
const ItemScopeIndexContext = createContext<string | undefined>(undefined);

// Per-section identity, surfaced separately from the item value/index path so
// existing `useItemScope()` consumers are untouched. Sibling result repeaters
// share one data source and differ only by `iteratorPath` bucket; threading the
// bucket here lets `createBoundRender` refine the product `location` per section
// instead of shipping `dexp-results-page` for all of them. A child repeater
// inherits the parent section when it declares none.
const ItemScopeSectionContext = createContext<string | undefined>(undefined);

export interface ItemScopeProviderProps {
  /**
   * The per-row data slice a `platform.dataRepeater` renders for each
   * item in its array source. May be any value — the binding resolver
   * uses {@link getByPath} which handles non-object shapes silently.
   */
  readonly item: unknown;
  /**
   * Zero-based row index within the enclosing repeater. Optional so
   * legacy / test mounts that don't supply it keep the non-repeater
   * `stableKey` semantics. Composed with any parent repeater's path
   * to stay unique under nesting.
   */
  readonly index?: number;
  // Enclosing repeater's section/bucket (e.g. `'recommended'`); inherits the
  // parent provider's section when omitted.
  readonly section?: string;
  readonly children: React.ReactNode;
}

/**
 * Scaffolding for the repeater-scope context (child SAD ADR-13).
 *
 * The production `<platform.dataRepeater>` component lands in DB-2.6
 * (EDUN-10693) and will wrap each rendered row in this provider.
 * Shipping the context now lets DB-1.4 tests exercise
 * `_bind: { source: 'item', … }` paths end-to-end, and guarantees the
 * binding enhancer's public API stays stable when the repeater lands.
 */
export const ItemScopeProvider: React.FC<ItemScopeProviderProps> = ({
  item,
  index,
  section,
  children,
}) => {
  const parentPath = useContext(ItemScopeIndexContext);
  const parentSection = useContext(ItemScopeSectionContext);
  const indexPath =
    index === undefined
      ? parentPath
      : parentPath === undefined
        ? String(index)
        : `${parentPath}.${index}`;
  const sectionValue = section ?? parentSection;
  return (
    <ItemScopeContext.Provider value={item}>
      <ItemScopeIndexContext.Provider value={indexPath}>
        <ItemScopeSectionContext.Provider value={sectionValue}>
          {children}
        </ItemScopeSectionContext.Provider>
      </ItemScopeIndexContext.Provider>
    </ItemScopeContext.Provider>
  );
};

/**
 * Return the nearest enclosing repeater item, or `undefined` when the
 * consumer is rendered outside a {@link ItemScopeProvider}. Bindings
 * referencing `source: 'item'` degrade silently when undefined — the
 * prop resolves to `undefined` and HEN UI defaults take over.
 */
export function useItemScope(): unknown {
  return useContext(ItemScopeContext);
}

/**
 * Return the nearest enclosing repeater row's identity path (e.g.
 * `"0"`, `"2"`, or `"0.3"` under nesting), or `undefined` when
 * rendered outside a {@link ItemScopeProvider}. Consumed by
 * `createBoundRender` to make each repeated card's `Trackable`
 * fire-once `stableKey` unique so per-row `ProductViewed` events are
 * not deduped against their siblings.
 */
export function useItemScopeIndexPath(): string | undefined {
  return useContext(ItemScopeIndexContext);
}

// Nearest enclosing repeater's section/bucket, or `undefined` outside a
// sectioned `ItemScopeProvider`. Used by `createBoundRender` to refine `location`.
export function useItemScopeSection(): string | undefined {
  return useContext(ItemScopeSectionContext);
}
